import CardCommon from '@op/components/CardCommon';
import React, { useEffect, useMemo, useState } from 'react';
import { Col, Row, Segment } from 'tea-component';
import useLanguage from '@op/hooks/useLanguage';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import KeyMetricsItem from '../component/KeyMetricsItem';
import {
  ByKeyMetricsType,
  CommunityOptionsType,
  getCommunityOptions,
  keyMetricsOptionsMap,
  keyMetricsSplitOptions,
  KeyMetricsType,
  streamingKeyMetricMap,
} from './constants';
import LeftChart from './LeftChart';
import RightChart from './RightChart';
import './index.scss';
import { useGetRankList } from '../hooks/rank';
import { useMetricsChannel } from '@op/hooks/useMetricsChannel';

export default function Community({
  filter,
  gameInfo,
  officialAccount,
  games,
  gameData,
  showRank,
  defaultTab,
  canNavigateToGame,
}: {
  filter: any;
  gameInfo?: any;
  officialAccount: Array<string>;
  gameData: any;
  games: Array<any>;
  showRank: boolean;
  defaultTab?: CommunityOptionsType;
  canNavigateToGame?: boolean;
}) {
  const { formatMessage } = useLanguage();
  const [tab, setTab] = useState<CommunityOptionsType>(defaultTab || 'total');
  const [keyMetric, setKeyMetric] = useState<{
    value: KeyMetricsType;
    format?: (value: any) => string;
  }>({ value: 'impressions' });
  const [keyMetricText, setKeyMetricText] = useState<string>(
    'Potential Impressions',
  );
  const [byType, setByType] = useState<{ value: ByKeyMetricsType }>({
    value: 'by_channel',
  });
  const [splitOptions, setSplitOptions] = useState<
    { text: string; value: string }[]
  >([]);

  function changeByTypeAndOptions(key: KeyMetricsType) {
    const list = keyMetricsSplitOptions[key].filter(
      ({ tabs }) => !tabs || tabs?.includes(tab),
    );

    setSplitOptions(list);
    setByType({ value: list[0]?.value });
  }

  function getOtherParams(tab: CommunityOptionsType) {
    const otherParams: any = {};
    if (tab === 'official_accounts') {
      otherParams.social_media_by_account = officialAccount;
    }
    if (tab === 'posts') {
      otherParams.review_types = ['posts_text', 'sub_text'];
    }
    if (tab === 'videos') {
      otherParams.review_types = ['posts_video', 'sub_video'];
    }

    return otherParams;
  }

  const paramsKeyMetric: string =
    tab === 'streaming' && streamingKeyMetricMap[keyMetric.value]
      ? (streamingKeyMetricMap[keyMetric.value] as string)
      : keyMetric.value;

  return (
    <CardCommon
      title={
        <span style={{ fontSize: 20 }}>
          {formatMessage({ id: 'Community' })}
        </span>
      }
      contentCard
      className="card-common-dbo-community"
    >
      <CardCommon className="card-common-dbo-key-metrics">
        <Segment
          value={tab}
          onChange={(value: CommunityOptionsType) => {
            setTab(value);
            buriedBtnPoint('KMCM_TypeTAB', value);
          }}
          options={getCommunityOptions({
            formatMessage,
            logId: 'KMCM_GotoPage',
            getOtherParams,
            filter,
            tab,
          })}
          className="dbo-community-segment"
        />

        <KeyMetricsList
          filter={filter}
          getOtherParams={getOtherParams}
          tab={tab}
          keyMetric={keyMetric}
          setKeyMetric={setKeyMetric}
          games={games}
          gameData={gameData}
          gameInfo={gameInfo}
          setKeyMetricText={setKeyMetricText}
          changeByTypeAndOptions={changeByTypeAndOptions}
          showRank={showRank}
          canNavigateToGame={canNavigateToGame}
          logId="KMCM_Metrics"
          rankLogId="KMCM_CompetitorRanking"
        ></KeyMetricsList>

        <Row className="dbo-community-metrics-chart">
          <Col>
            <LeftChart
              getOtherParams={getOtherParams}
              tab={tab}
              byType={byType}
              options={splitOptions}
              setByType={setByType}
              keyMetric={keyMetric}
              title={keyMetricText}
              paramsKeyMetric={paramsKeyMetric}
              filter={filter}
              gameInfo={gameInfo}
              detailPage={byType.value !== 'by_follower_range'}
            ></LeftChart>
          </Col>
          <Col span={tab === 'streaming' ? 0 : 8}>
            <RightChart
              filter={filter}
              tab={tab}
              isPercent={keyMetric.value === 'engagement_rate'}
              getOtherParams={getOtherParams}
              metrics={keyMetric}
              formatValue={keyMetric.format}
            ></RightChart>
          </Col>
        </Row>
      </CardCommon>
    </CardCommon>
  );
}

function KeyMetricsList({
  tab,
  filter,
  keyMetric,
  setKeyMetric,
  setKeyMetricText,
  games,
  gameData,
  getOtherParams,
  showRank,
  logId,
  rankLogId,
  gameInfo,
  canNavigateToGame,
  changeByTypeAndOptions,
}: {
  tab: CommunityOptionsType;
  filter: any;
  keyMetric: { value: KeyMetricsType };
  setKeyMetric: (item: {
    value: KeyMetricsType;
    format?: (value: any) => string;
  }) => void;
  setKeyMetricText: (value: string) => void;
  changeByTypeAndOptions: (value: any) => void;
  gameData: any;
  games: Array<any>;
  getOtherParams: (tab: CommunityOptionsType) => any;
  showRank: boolean;
  logId?: string;
  rankLogId?: string;
  gameInfo?: any;
  canNavigateToGame?: boolean;
}) {
  const [keyMetricsOptions, setKeyMetricsOptions] = useState<Array<any>>([]);
  const { formatMessage } = useLanguage();
  const MergeChannelIconElement = useMetricsChannel({ gameInfo });

  useEffect(() => {
    const options = keyMetricsOptionsMap[tab];
    setKeyMetricsOptions(options);
    const { format, key, text } = options[0] || {};

    changeByTypeAndOptions(key);
    setKeyMetric({ value: key, format });
    setKeyMetricText(text);
  }, [tab]);

  const params = useMemo(() => {
    return {
      ...filter,
      ...getOtherParams(tab),
      games,
      tab_type: tab,
      stats_types: ['community'],
    };
  }, [filter, tab]);

  const { data, loading, getRankList } = useGetRankList({
    filter: params,
    gameData,
  });

  return (
    <CardCommon
      className="dbo-community-metrics"
      loading={loading}
      loadingHeight={136}
      contentCard
    >
      {keyMetricsOptions.map((metric) => {
        return (
          <KeyMetricsItem
            key={metric.key}
            item={metric}
            valueMap={data}
            filter={filter}
            keyMetric={keyMetric.value}
            getRankList={getRankList}
            canNavigateToGame={canNavigateToGame}
            MergeChannelIconElement={MergeChannelIconElement}
            // getMergeChannelIconElement={getMergeChannelIconElement}
            setKeyMetric={(value, text) => {
              logId && buriedBtnPoint(logId, value);
              setKeyMetric({ value, format: metric.format });
              setKeyMetricText(text);
              changeByTypeAndOptions(value);
            }}
            showRank={showRank}
            title={formatMessage({ id: metric.text })}
            logId={rankLogId}
          ></KeyMetricsItem>
        );
      })}
    </CardCommon>
  );
}
